import {useEffect,useState} from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { RouteContent } from "./RouteContent";
import {
  CalendarDays, LogOut, FileText, FolderOpen, Globe, LayoutDashboard, Plug, Building2, Inbox,
  CreditCard, KeyRound, Sun, Moon, Table2, LineChart, LayoutTemplate, Link2, Wrench, Clapperboard, Palette, Wand2, Images, FileSignature, BookOpen,
  TrendingUp, Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { CommandPalette, CommandPaletteTrigger } from "@/components/CommandPalette";
import { FeedbackRecorder } from "@/components/feedback/FeedbackRecorder";
import { ScopeSelector } from "@/components/ScopeSelector";
import { recentSites, useWebsites, websitePath } from "@/lib/websites";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { Shell } from "@/components/shell/Shell";
import { useNavVersion } from "@/lib/navChrome";

/**
 * The page editor (/sites/:siteId/pages/:pageId/edit) wants the entire viewport.
 * Webflow-style: no outer sidebar, no container constraints, no padding —
 * the editor's own chrome is sufficient. We detect the route here and skip
 * the standard Layout chrome entirely for it.
 */
const EDITOR_PATH_RE = /^\/sites\/\d+\/pages\/\d+\/edit\/?$/;

type NavItem = {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  disabled?: boolean;
  /** "/" prefix-matches every route, so the Overview link needs exact matching
   *  or it renders active on every page. */
  end?: boolean;
};

/**
 * THE ROWS AT THE TOP OF THE SIDEBAR, always visible: Sites, Pages, Leads,
 * Analytics, Projects, Settings (the approved dashboard mock, 17 Oct 2026).
 * Sites is every website, whatever made it, one row each; there is no Ideas
 * row, and /ideas redirects to /sites. Analytics is one home: Session Replay,
 * Experiments, Content performance, Search Console, Google Analytics,
 * Assistants, Audit and AI Visibility are tabs there, not rows here, and their
 * old routes keep working.
 *
 * Under them, the last few websites you opened, then More tools, collapsed.
 * Settings opens Connections, the accounts and per-site tools you set up once.
 */
const PRIMARY_NAV: NavItem[] = [
  { to: "/sites", label: "Sites", icon: Globe },
  { to: "/pages", label: "Pages", icon: FileText },
  { to: "/submissions", label: "Leads", icon: Inbox },
  { to: "/analytics", label: "Analytics", icon: LineChart },
  { to: "/projects", label: "Projects", icon: FolderOpen },
  { to: "/connections", label: "Settings", icon: Settings },
];

/** Built from PRIMARY_NAV, so the More tools groups never repeat a primary row. */
const PRIMARY_PATHS = PRIMARY_NAV.map((item) => item.to);


/**
 * FOUR GROUPS, BECAUSE TWELVE FLAT ITEMS IS NOT A MENU, IT IS A LIST.
 *
 * The old sidebar was twelve destinations in one column, ordered by roughly when
 * each was built. Nothing in it said that Submissions and Keywords are both rows
 * in tables, or that Search Console and Bots are two readings of the same
 * traffic, or that Connections and Integrations are the same kind of thing — so
 * the only way to find anything was to have already known where it was. Twelve
 * items is also exactly the length at which a person stops reading a list and
 * starts scanning it, and there is nothing to scan when every row looks alike.
 *
 * The grouping is by WHAT YOU ARE DOING, not by which service answers the call:
 *
 *   DATA      the rows you own — whatever put them there
 *   PUBLISH   the things anyone outside can open
 *   ANALYTICS what happened after you published, one source per item
 *   SETTINGS  the plumbing, which you touch once and then forget
 *
 * ANALYTICS IS FIVE ITEMS RATHER THAN ONE CALLED "INSIGHTS" holding five tabs.
 * A tab is invisible from the sidebar: "where do I see Search Console numbers"
 * had the answer "click Insights, then the second tab", which is a thing you can
 * only know by having been told. Each is now a URL, a nav item and a page with
 * its own heading — see the tab note in Insights.tsx.
 *
 * Overview sits ABOVE the groups, ungrouped, because it is the app's front door
 * rather than a member of any category. There is no "Settings" placeholder item
 * any more: a greyed-out row called Settings inside a group called SETTINGS said
 * nothing except that somebody had not finished.
 */
const NAV_GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: "Data",
    items: [
      // FEEDS IS NOT A PLACE. A feed is a published TABLE — the same object with
      // a publication attached — so it lives on the table, under Tables, as the
      // Publish tab. Two nav items showing one tree under two vocabularies is
      // what made "is a feed a table?" a question anybody had to ask. The /feeds
      // route still exists for anything linking to it.
      { to: "/tables", label: "Tables", icon: Table2 },
      { to: "/submissions", label: "Submissions", icon: Inbox },
      ...(import.meta.env.VITE_SCHEDULING_ENABLED === "true" ? [{to: "/scheduling", label: "Scheduling", icon: CalendarDays}] : []),
      // The old Keywords page folded into keyword research (/keywords redirects there).
      { to: "/keywords/research", label: "Keyword research", icon: TrendingUp },
    ],
  },
  {
    title: "Publish",
    items: [
      { to: "/sites", label: "Sites", icon: Globe },
      { to: "/pages", label: "Pages", icon: FileText },
      { to: "/social", label: "Social", icon: Clapperboard },
      // Interfaces WERE only a tab inside one table, which meant you could not
      // answer "what have I shared, and with whom" without opening every table
      // in turn. See routes/Interfaces.tsx.
      { to: "/interfaces", label: "Interfaces", icon: LayoutTemplate },
      // EVERY WAY A WORKSPACE IS OPENED TO SOMEBODY ELSE, in one list: booking
      // pages, forms, interfaces, shared lists, assistant links, videos. Two of
      // those had no screen before this one. See routes/Links.tsx.
      { to: "/links", label: "Links", icon: Link2 },
      // UNDER PUBLISH RATHER THAN UNDER SETTINGS, because a fix is a change to
      // what the site says and not to how the account is configured. It sits
      // last: you publish, then you find out what is missing.
      { to: "/fixes", label: "Fixes", icon: Wrench },
    ],
  },
  {
    // MEDIA IS ITS OWN GROUP, not an item under Publish, because Bloxx is being
    // built as modules (analytics, sites, media, data sharing) and the sidebar is
    // where somebody first sees which ones exist. Studio is its first screen;
    // generated images and carousels land here next.
    title: "Media",
    items: [
      { to: "/media/brands", label: "Brands", icon: Palette },
      { to: "/media/create", label: "Create", icon: Wand2 },
      { to: "/media/library", label: "Library", icon: Images },
      { to: "/studio", label: "Studio", icon: Clapperboard },
      // Recordings of what somebody did, read as feedback or as a guide. Under
      // Media because it is a recording, next to the one that makes videos.
      { to: "/guides", label: "Guides", icon: BookOpen },
    ],
  },
  {
    // bloxx.work, as its own module: a PDF goes out, a person's AI fills the
    // details, the person signs. See routes/Work.tsx.
    title: "Work",
    items: [{ to: "/work", label: "Documents", icon: FileSignature }],
  },
  {
    title: "Settings",
    items: [
      ...(import.meta.env.VITE_BUSINESS_IDENTITY === "1" ? [{ to: "/businesses", label: "Businesses", icon: Building2 }] : []),
      { to: "/connections", label: "Connections", icon: Plug },
      { to: "/connected-apps", label: "Connections", icon: KeyRound },
      { to: "/billing", label: "Billing", icon: CreditCard },
      { to: "/settings/domains", label: "Domain registrant", icon: Globe },
      { to: "/projects", label: "Projects", icon: FolderOpen },
    ],
  },
];

const OVERVIEW: NavItem = { to: "/overview", label: "Overview", icon: LayoutDashboard, end: true };

function NavRow({ item }: { item: NavItem }) {
  const Icon = item.icon;
  if (item.disabled) {
    return (
      <div
        className="flex cursor-not-allowed items-center gap-2 rounded-md px-3 py-1.5 text-sm text-muted-foreground/60"
        title="Coming soon"
      >
        <Icon className="h-4 w-4" />
        <span>{item.label}</span>
        <span className="ml-auto text-[10px] uppercase tracking-wide">soon</span>
      </div>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          // py-1.5 rather than py-2: four headings cost vertical space, and this
          // buys most of it back without the rows becoming a hit-target problem.
          "relative flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors",
          isActive
            ? // A CORAL RULE PLUS A WEIGHT CHANGE, not a filled grey block. The
              // grey fill was the same tone as hover, so the page you were on and
              // the row under the cursor looked identical — and it made a whole
              // row of chrome heavier than the thing it was pointing at. Accent
              // is the selection colour throughout the admin.
              "bg-accent/[0.07] font-medium text-foreground"
            : "font-normal text-muted-foreground hover:bg-secondary hover:text-foreground",
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && <span className="absolute inset-y-1 left-0 w-0.5 bg-accent" aria-hidden />}
          <Icon className={cn("h-4 w-4 shrink-0", isActive && "text-accent")} />
          <span className="truncate">{item.label}</span>
          {item.badge && (
            <span className="ml-auto rounded-full bg-accent/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent">
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

/** The last websites you opened (lib/websites.ts recentSites), then the rest of your list, three in all. */
function RecentSites() {
  const websites = useWebsites();
  const { pathname } = useLocation();
  const all = websites.data?.websites ?? [];
  if (!all.length) return null;
  const seen = recentSites();
  const rows = [...seen.map((ref) => all.find((w) => w.ref === ref)).filter((w): w is NonNullable<typeof w> => !!w), ...all].filter((w, i, list) => list.findIndex((x) => x.ref === w.ref) === i).slice(0, 3);
  return (
    <div className="mt-4">
      <p className="px-3 pb-1.5 pt-1 text-[11px] font-medium text-muted-foreground/80">Recent sites</p>
      <div className="flex flex-col gap-0.5">
        {rows.map((w) => {
          const to = websitePath(w);
          const on = pathname === to || pathname.startsWith(to + "/");
          return (
            <NavLink key={w.ref} to={to} className={cn("flex items-center gap-2.5 rounded-md px-3 py-1.5 text-[13.5px] transition-colors", on ? "bg-secondary font-medium text-foreground" : "text-foreground/80 hover:bg-secondary")}>
              <span aria-hidden className="flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border bg-background text-[10.5px] font-semibold text-foreground/80">{w.name.charAt(0).toUpperCase()}</span>
              <span className="truncate">{w.name}</span>
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}

/**
 * NAV V2 IS THE SHELL NOW (components/shell, lib/navMap.ts): a section rail, a workspace and site switcher, and
 * each section's pages as horizontal tabs. The sidebar below is the classic one, kept for one release behind
 * ?nav=classic (or Account, Classic navigation) and remembered until ?nav=v2. The page editor takes the whole
 * viewport under either.
 */
export function Layout() {
  const version = useNavVersion();
  const { pathname } = useLocation();
  if (EDITOR_PATH_RE.test(pathname)) {
    return (
      <div className="h-screen w-screen overflow-hidden bg-secondary/30">
        <RouteContent />
        {/* Mounted here too. The editor drops the rest of the chrome on
            purpose, but jumping to another page is exactly what you want while
            editing one, and the shortcut working everywhere except the screen
            you spend the most time on would be the wrong exception. */}
        <CommandPalette />
      </div>
    );
  }
  return version === "classic" ? <ClassicLayout /> : <Shell />;
}

function ClassicLayout() {
  useEffect(() => { performance.mark("bloxx:shell-visible"); }, []);
  const { user, logout } = useAuth();
  const { setTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNav,setMobileNav]=useState(false);
  useEffect(()=>setMobileNav(false),[location.pathname]);
  // Screens whose content IS the page rather than a document on it.
  const fullBleed = location.pathname.startsWith("/tables");

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (() => {
    const f = user?.first_name?.charAt(0) ?? "";
    const l = user?.last_name?.charAt(0) ?? "";
    if (f || l) return (f + l).toUpperCase();
    return user?.email?.charAt(0).toUpperCase() ?? "?";
  })();

  return (
    <div className="grid h-full grid-cols-[minmax(0,1fr)] md:grid-cols-[240px_minmax(0,1fr)] grid-rows-[minmax(56px,auto)_minmax(0,1fr)] bg-secondary/30 text-foreground">
      {/* Top bar */}
      <header className="col-span-1 md:col-span-2 flex flex-wrap gap-2 items-center justify-between border-b bg-background px-4 py-2">
        <div className="flex items-center gap-2">
          <img
            src="https://res.cloudinary.com/dqa47jizr/image/upload/q_auto/f_auto/v1777471110/bloxx-logo_gedexe.png"
            alt="Bloxx"
            className="h-7 w-7 rounded dark:invert"
          />
          <span className="text-base font-semibold tracking-tight">Bloxx</span>
          <Button className="md:hidden" variant="ghost" size="sm" aria-expanded={mobileNav} aria-controls="main-navigation" onClick={()=>setMobileNav(!mobileNav)}>Menu</Button>
          <span className="hidden md:inline ml-2 rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-foreground">
            admin
          </span>
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          {/* The scope, then the search it scopes. In that order because the
              search reads the scope, not the other way round. */}
          <div className="hidden md:flex items-center gap-3"><ScopeSelector /><CommandPaletteTrigger /></div>
          {/* A plain light/dark switch. It previously cycled
              light -> dark -> system, which meant up to three clicks to reach
              the state you wanted and no indication of where you were in the
              cycle. "system" is still the DEFAULT until you touch this; the
              first click just makes a choice explicit. */}
          <button
            type="button"
            title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            onClick={() => setTheme(isDark ? "light" : "dark")}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <span className="hidden max-w-40 truncate text-sm text-muted-foreground sm:inline">
            {user?.email}
          </span>
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-xs font-semibold text-[#242424]">
            {initials}
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="mr-1.5" />
            Log out
          </Button>
        </div>
      </header>

      {/* Sidebar. Scrolls on its own: four groups plus a heading each is taller
          than twelve flat rows were, and a short laptop should not cut Settings
          off with nothing to say it is there. */}
      <aside id="main-navigation" onKeyDown={e=>{if(e.key==="Escape")setMobileNav(false);}} className={cn("md:row-start-2 md:static md:flex min-h-0 flex-col overflow-y-auto border-r bg-background", mobileNav?"fixed z-40 left-0 top-14 bottom-0 w-64 flex shadow-lg":"hidden")}>
        <div className="md:hidden flex flex-wrap gap-2 p-3"><ScopeSelector /><CommandPaletteTrigger /></div>
        <nav className="flex flex-col p-3">
          <div className="flex flex-col gap-0.5">
            {PRIMARY_NAV.map((item) => <NavRow key={item.to} item={item} />)}
          </div>
          <RecentSites />
          <details className="mt-4" open={location.pathname===OVERVIEW.to||NAV_GROUPS.some(g=>g.items.some(i=>!PRIMARY_PATHS.includes(i.to)&&location.pathname.startsWith(i.to.split('?')[0])))}>
            <summary className="cursor-pointer px-3 py-2 text-[12.5px] text-muted-foreground hover:text-foreground">More tools</summary>
            <NavRow item={OVERVIEW} />
            {NAV_GROUPS.map(group=><div key={group.title} className="mt-4">
              <p className="px-3 pb-1.5 pt-1 font-mono text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">{group.title}</p>
              {group.items.filter(item=>!PRIMARY_PATHS.includes(item.to)).map(item=><NavRow key={item.to} item={item}/>)}
            </div>)}
          </details>
        </nav>

        <p className="mx-3 mb-3 mt-auto flex flex-wrap gap-3 px-3 text-xs text-muted-foreground">
          <NavLink className="hover:underline" to={`${location.pathname}${location.search ? `${location.search}&` : "?"}nav=v2`}>New navigation</NavLink>
          <a className="hover:underline" href="https://itsbloxx.com/privacy" target="_blank" rel="noreferrer">Privacy policy</a>
          <a className="hover:underline" href="https://itsbloxx.com/terms" target="_blank" rel="noreferrer">Terms</a>
        </p>
      </aside>

      {/* Main */}
      {/*
        FULL BLEED FOR THE DATA SCREENS. Every route used to be wrapped in
        `max-w-6xl mx-auto`, which is right for a form or a report and wrong for
        a spreadsheet: it capped the grid at 1152px and put dead space either side
        of it on any real monitor, so a table with six fields was already
        scrolling sideways. Routes that ARE documents keep the measure.
      */}
      {/* app-main leaves the feedback button's corner free (index.css, B5): padded by
          --dock-bottom, and on a phone the scroll area stops above the button. */}
      <main className={cn("app-main row-start-2 min-h-0 min-w-0", fullBleed ? "overflow-hidden" : "overflow-y-auto")}>
        {fullBleed ? (
          <RouteContent />
        ) : (
          <div className="container mx-auto max-w-6xl px-3 md:px-6 py-6 md:py-8">
            <RouteContent />
          </div>
        )}
      </main>

      <CommandPalette />
      {/* Recording feedback, from anywhere in the app: the whole point is that
          you press it at the moment something annoys you, which is never on a
          screen you navigated to on purpose. Ctrl/Cmd + Shift + F.

          IT WAS ONE ACCOUNT WHILE A REPORT HAD NOWHERE TO GO. Now that sending
          one keeps it in the workspace (src/captures.ts), anybody in the
          workspace can report something and somebody will see it. The wording
          went with the gate: "Copy for Claude" was a sentence for one reader.
          What is still deliberately absent is any notification, so somebody has
          to go and look, which is the next thing to fix rather than a thing to
          pretend about. */}
      <FeedbackRecorder />
    </div>
  );
}
